import React, { useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Flex, Input, Popover, message } from 'antd';
import { PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { LeadTagChip, resolveLeadTags } from './leadTags';

/**
 * The tags on one lead row, editable in place.
 *
 * Click the cell for a popover listing every tag; ticking or unticking one
 * saves straight away. Typing a name that doesn't exist yet offers to create
 * it and put it on the lead in one go.
 *
 * `tagIds` / `details` are the row's `tags` / `tags_detail`, `allTags` the
 * ordered list from /lead/tags/ (null until loaded). `onSave(ids)` must
 * resolve once the row carries what the server saved; `onCreateTag(name)`
 * resolves with the new tag.
 */
const LeadTagsCell = ({ tagIds = [], details = [], allTags = null, onSave, onCreateTag }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  // The selection as last picked, shown while saves are in flight so the
  // cell doesn't blink back to the stale row.
  const [optimistic, setOptimistic] = useState(null);
  const [saving, setSaving] = useState(false);

  // Saves run one after another, each sending the full list, so the last
  // pick always wins even if the user clicks quickly.
  const queue = useRef(Promise.resolve());
  const pending = useRef(0);

  // Once the list has loaded, ids it doesn't know are tags deleted since the
  // row was fetched - drop them so a save doesn't send them back.
  const selectedIds = useMemo(() => {
    const ids = optimistic ?? tagIds ?? [];
    return allTags ? ids.filter((id) => allTags.some((tag) => tag.id === id)) : ids;
  }, [optimistic, tagIds, allTags]);
  const shown = useMemo(
    () => resolveLeadTags(selectedIds, details, allTags),
    [selectedIds, details, allTags]
  );

  const term = search.trim().toLowerCase();
  const matches = (allTags || []).filter((tag) => tag.name.toLowerCase().includes(term));
  const exactMatch = (allTags || []).some((tag) => tag.name.toLowerCase() === term);

  // One toast per lead, updated in place, so quick ticking doesn't stack up
  // a message per click: "Updating tags..." then "Tags updated" once the
  // last queued save lands.
  const toastKey = useRef(`lead-tags-${Math.random().toString(36).slice(2)}`);
  const failed = useRef(false);

  const save = (ids) => {
    setOptimistic(ids);
    setSaving(true);
    if (pending.current === 0) failed.current = false;
    pending.current += 1;
    message.open({ key: toastKey.current, type: 'loading', content: 'Updating tags...', duration: 0 });
    queue.current = queue.current
      .then(() => onSave(ids))
      .catch(() => {
        failed.current = true;
      })
      .finally(() => {
        pending.current -= 1;
        if (pending.current === 0) {
          // Hand display back to the row: saved, or never changed.
          setOptimistic(null);
          setSaving(false);
          message.open({
            key: toastKey.current,
            type: failed.current ? 'error' : 'success',
            content: failed.current ? 'Could not update tags' : 'Tags updated',
            duration: 2,
          });
        }
      });
  };

  const toggle = (tagId) => {
    save(selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : [...selectedIds, tagId]);
  };

  const createAndAdd = async () => {
    const name = search.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const tag = await onCreateTag(name);
      setSearch('');
      save([...selectedIds, tag.id]);
    } catch (e) {
      const errors = e?.response?.data;
      message.error(errors?.name?.[0] || 'Could not create the tag');
    }
    setCreating(false);
  };

  const content = (
    <div style={{ width: 240 }} onClick={(e) => e.stopPropagation()}>
      <Input
        size="small"
        allowClear
        autoFocus
        placeholder="Search or create a tag"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onPressEnter={() => {
          if (matches.length === 1 && term) toggle(matches[0].id);
          else if (!exactMatch) createAndAdd();
        }}
      />
      <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8 }}>
        {allTags === null ? null : matches.length ? (
          matches.map((tag) => (
            <Flex
              key={tag.id}
              align="center"
              gap={8}
              style={{ padding: '3px 2px', cursor: 'pointer' }}
              onClick={() => toggle(tag.id)}
            >
              <Checkbox checked={selectedIds.includes(tag.id)} />
              <LeadTagChip tag={tag} />
            </Flex>
          ))
        ) : (
          !term && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tags yet" />
        )}
      </div>
      {term && !exactMatch && onCreateTag && (
        <Button
          type="dashed"
          size="small"
          block
          icon={<PlusOutlined />}
          loading={creating}
          onClick={createAndAdd}
          style={{ marginTop: 8 }}
        >
          Create "{search.trim()}"
        </Button>
      )}
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
      content={content}
      destroyOnHidden
    >
      <div
        title="Click to edit tags"
        onClick={(e) => e.stopPropagation()}
        style={{ cursor: 'pointer', minHeight: 22, minWidth: 120, opacity: saving ? 0.6 : 1 }}
      >
        {shown.length ? (
          shown.map((tag) => <LeadTagChip key={tag.id} tag={tag} />)
        ) : (
          <span style={{ color: '#bfbfbf' }}>
            <TagsOutlined /> Add
          </span>
        )}
      </div>
    </Popover>
  );
};

export default LeadTagsCell;
